// In-meeting + lobby surfaces for StandUp Phase 3.
//
// Three components live in this file because they share helpers and we want
// to ship them as one tight slice:
//   - StandUpRouter: tiny dispatcher that decides host vs participant view
//     after the loader. Bounces 403 to participant view.
//   - StandUpHost: the host's perspective on the meeting (Frame 3.1). Owns
//     the meeting timer, item buckets, and the lobby drawer.
//   - StandUpParticipant: the participant entry/read-only surface
//     (Frames 3.2 / 3.3). Submits drafts + finalises.
//   - StandUpJoin (default export of /j/:token): public magic-link landing
//     page that resolves the token and either routes into the lobby or
//     the participant view directly.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi, POLL_MS } from "@/lib/standup-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Play, Pause, Plus, Trash, Check, X as XIcon, CaretLeft, CaretRight, Lock,
  Bell, ShareNetwork, Star, Link as LinkIcon, Cube, Rocket, ListPlus, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function useProgressPolling(sessionId) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    const fetchOnce = async () => {
      try {
        const r = await standupApi.progress(sessionId);
        if (live) setData(r.data);
      } catch (_) { /* ignore — endpoint may 403 briefly during admit */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => { live = false; clearInterval(id); };
  }, [sessionId]);
  return data;
}

// -------------------- Router --------------------

export function StandUpRouter() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null); // "host" | "participant"

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    // Cheap probe — try the host-only lobby endpoint. 200 → host. 403 → participant.
    standupApi.lobby(sessionId)
      .then(() => setDecision("host"))
      .catch(() => setDecision("participant"));
  }, [user, sessionId]);

  useEffect(() => {
    if (!decision) return;
    navigate(`/standup/session/${sessionId}/${decision}`, { replace: true });
  }, [decision, navigate, sessionId]);

  return <div className="min-h-screen grid place-items-center text-slate-500">Routing…</div>;
}

// -------------------- Host View --------------------

export function StandUpHost() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const progress = useProgressPolling(sessionId);
  const [standup, setStandup] = useState(null);
  const [lobby, setLobby] = useState({ waiting: [] });
  const [participantIndex, setParticipantIndex] = useState(0);
  const [nextStepsDraft, setNextStepsDraft] = useState("");
  const [parkingLotDraft, setParkingLotDraft] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  // Pull the parent standup for its questions + name
  useEffect(() => {
    if (!progress?.status) return;
    // Find standup via progress (we don't have its id in progress). Cheap call:
    // /sessions/active returns standup_name. We just refresh once.
    standupApi.activeSessions().then((r) => {
      const me = (r.data.sessions || []).find((s) => s.session_id === sessionId);
      if (me) setStandup(me);
    });
  }, [progress?.status, sessionId]);

  // Poll the lobby every POLL_MS
  useEffect(() => {
    if (!user) return;
    let live = true;
    const fn = async () => {
      try {
        const r = await standupApi.lobby(sessionId);
        if (live) setLobby(r.data);
      } catch (_) { /* ignored */ }
    };
    fn();
    const id = setInterval(fn, POLL_MS);
    return () => { live = false; clearInterval(id); };
  }, [user, sessionId]);

  async function admit(email) {
    try {
      await standupApi.admit(sessionId, email);
      toast.success(`${email} admitted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not admit");
    }
  }
  async function deny(email) {
    try {
      await standupApi.deny(sessionId, email);
    } catch (_) {}
  }

  async function addItem(bucket, text, clear) {
    if (!text.trim()) return;
    try {
      await standupApi.addItem(sessionId, { bucket, text: text.trim() });
      clear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  }

  const participants = useMemo(() => Object.entries(progress?.participants || {}), [progress]);
  const focused = participants[participantIndex] || null;

  if (authLoading || !user) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const meetingStarted = progress?.status === "live";

  return (
    <div className="min-h-screen bg-[#0B0B14] text-slate-100 relative" data-testid="standup-host">
      <div className="px-6 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <Button
            onClick={async () => {
              if (!meetingStarted) {
                try { await standupApi.startSession(sessionId); toast.success("Meeting started"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
              }
            }}
            className="h-9 bg-sky-500 hover:bg-sky-600 text-white text-xs"
            data-testid="host-start-meeting"
          >
            <Play size={11} weight="fill" className="mr-1" /> {meetingStarted ? "Meeting Live" : "Start Meeting"}
          </Button>
          <span className="font-mono text-base">{formatElapsed(progress?.started_at)}</span>
          {meetingStarted && (
            <Button
              onClick={async () => {
                if (!window.confirm("End the meeting now? The summary will be generated and shared.")) return;
                try {
                  await standupApi.endSession(sessionId);
                  toast.success("Meeting ended");
                  navigate(`/standup/session/${sessionId}/summary`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not end meeting");
                }
              }}
              className="h-9 bg-rose-500 hover:bg-rose-600 text-white text-xs"
              data-testid="host-end-meeting"
            >
              End Meeting →
            </Button>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className="relative ml-4 h-9 px-3 rounded-md bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-100 inline-flex items-center gap-2"
                data-testid="lobby-trigger"
              >
                <Bell size={12} weight="bold" />
                Lobby
                {(lobby.waiting?.length || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-[10px] font-bold grid place-items-center">
                    {lobby.waiting.length}
                  </span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white text-slate-900">
              <SheetHeader>
                <SheetTitle>Lobby ({lobby.waiting?.length || 0} waiting)</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {(lobby.waiting || []).length === 0 && (
                  <div className="text-sm text-slate-500">No one's waiting. We'll ping you when someone joins via the link.</div>
                )}
                {(lobby.waiting || []).map((l) => (
                  <div key={l.email} className="flex items-center justify-between rounded-md border border-slate-200 p-3" data-testid={`lobby-row-${l.email}`}>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{l.name || l.email}</div>
                      <div className="text-xs text-slate-500">{l.email}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => deny(l.email)} variant="outline" className="h-8 px-3 text-xs" data-testid={`lobby-deny-${l.email}`}>Deny</Button>
                      <Button onClick={() => admit(l.email)} className="h-8 px-3 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`lobby-admit-${l.email}`}>Admit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr,260px] gap-4">
          {/* Left participant card */}
          <ParticipantProgressCard tile={participants[participantIndex - 1]} side="left" onPrev={() => setParticipantIndex(Math.max(0, participantIndex - 1))} questions={standup?.questions} />

          {/* Center: meeting card */}
          <div className="rounded-2xl bg-violet-600/20 backdrop-blur p-4">
            <div className="rounded-xl bg-white text-slate-900 p-6">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <LinkIcon size={11} /> Link to Task
                <span className="ml-2">Last updated {new Date().toLocaleDateString()}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="h-8 w-8 rounded-md bg-violet-100 grid place-items-center">
                  <Rocket size={14} weight="fill" className="text-violet-700" />
                </span>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-700 to-sky-500 bg-clip-text text-transparent">
                  {standup?.standup_name || "Standup"}
                </h2>
              </div>
              <div className="mt-4 text-xs text-slate-500">
                {participants.length} Members · {progress?.items?.length || 0} Tasks
              </div>

              {/* Items */}
              <div className="mt-6 space-y-3">
                {(progress?.items || []).map((it) => (
                  <div key={it.id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check size={14} weight="bold" className="text-emerald-500" />
                      <span className="text-sm text-slate-800">Item {it.id}</span>
                    </div>
                    <span className={`font-mono text-sm ${it.elapsed_seconds > 90 ? "text-rose-500" : "text-emerald-500"}`}>
                      {formatSeconds(it.elapsed_seconds)}
                    </span>
                  </div>
                ))}
                {(!progress?.items || progress.items.length === 0) && (
                  <div className="text-xs text-slate-400">No items tracked yet. Use the timer to track time on individual items.</div>
                )}
              </div>

              {/* Next Steps + Parking Lot quick add */}
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <QuickAdd label="Next Steps" testid="add-next-steps" draft={nextStepsDraft} setDraft={setNextStepsDraft} onAdd={() => addItem("next_steps", nextStepsDraft, () => setNextStepsDraft(""))} items={progress?.next_steps} />
                <QuickAdd label="Parking Lot" testid="add-parking-lot" draft={parkingLotDraft} setDraft={setParkingLotDraft} onAdd={() => addItem("parking_lot", parkingLotDraft, () => setParkingLotDraft(""))} items={progress?.parking_lot} />
              </div>
            </div>
          </div>

          {/* Right participant card */}
          <ParticipantProgressCard tile={participants[participantIndex + 1]} side="right" onNext={() => setParticipantIndex(Math.min(participants.length - 1, participantIndex + 1))} questions={standup?.questions} />
        </div>
      </div>
    </div>
  );
}

function QuickAdd({ label, testid, draft, setDraft, onAdd, items }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{label} <span className="text-slate-400">({items?.length || 0})</span></div>
      <div className="mt-2 flex items-center gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`+ Add ${label.toLowerCase()}`} className="h-8 text-xs" data-testid={`${testid}-input`} />
        <Button onClick={onAdd} className="h-8 px-2 bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid={`${testid}-btn`}>
          <Plus size={11} weight="bold" />
        </Button>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-slate-700">
        {(items || []).map((it) => <li key={it.id} className="truncate">· {it.text}</li>)}
      </ul>
    </div>
  );
}

function ParticipantProgressCard({ tile, side, onPrev, onNext, questions }) {
  if (!tile) return (
    <div className={`hidden lg:flex items-center ${side === "left" ? "justify-end" : "justify-start"} text-slate-500 text-xs`} />
  );
  const [email, state] = tile;
  const counts = state?.counts || {};
  return (
    <div className="rounded-xl bg-white text-slate-900 p-4 shadow-md" data-testid={`participant-tile-${email}`}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-full text-white text-[11px] font-bold grid place-items-center" style={{ background: hashGradient(email) }}>
          {email[0].toUpperCase()}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{state?.name || email.split("@")[0]}</div>
          <div className="text-[10px] text-slate-500">Function/Role</div>
        </div>
        {side === "left" && <button onClick={onPrev} className="ml-auto h-7 w-7 rounded-full grid place-items-center bg-slate-100"><CaretLeft size={12} weight="bold" /></button>}
        {side === "right" && <button onClick={onNext} className="ml-auto h-7 w-7 rounded-full grid place-items-center bg-slate-100"><CaretRight size={12} weight="bold" /></button>}
      </div>

      <div className="mt-3 space-y-3">
        {(questions || []).map((q, i) => {
          const n = counts[q.id] || 0;
          return (
            <div key={q.id}>
              <div className="text-sm font-semibold text-sky-600 leading-tight">{q.text}</div>
              <div className="mt-1.5 flex gap-1.5">
                {Array.from({ length: n }).map((_, j) => (
                  <span key={j} className="h-6 w-6 grid place-items-center rounded-md bg-slate-50 border border-slate-200 text-[10px] font-semibold text-slate-700">
                    {i * 5 + j + 1}
                  </span>
                ))}
                {n === 0 && <span className="text-[10px] text-slate-400">No items yet</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-[10px] text-slate-500">
        <span>{state?.total_points || 0} Total Points</span>
        <span>{state?.completed || 0} Completed</span>
        <span>{state?.blockers || 0} Blockers</span>
      </div>
    </div>
  );
}

// -------------------- Participant View --------------------

export function StandUpParticipant() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const progress = useProgressPolling(sessionId);
  const [responses, setResponses] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [meta, setMeta] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  // Hydrate prior draft / submission
  useEffect(() => {
    if (!user) return;
    standupApi.myList(sessionId).then((r) => {
      if (r.data && !r.data.empty) {
        setResponses(r.data.responses || {});
        setSubmitted(!r.data.is_draft);
        setSavedAt(r.data.draft_saved_at);
      }
    }).catch(() => {});
    standupApi.activeSessions().then((r) => {
      const me = (r.data.sessions || []).find((s) => s.session_id === sessionId);
      if (me) setMeta(me);
    });
    // Daily quote — same line for every attendee, varies by date+session
    standupApi.quoteToday(sessionId).then((r) => setQuote(r.data)).catch(() => {});
  }, [user, sessionId]);

  const questions = meta?.questions || progress?.items?.questions || [];

  function addItem(qid) {
    setResponses((r) => {
      const arr = r[qid] || [];
      return { ...r, [qid]: [...arr, { id: `i${Date.now()}`, text: "", done: false, is_blocker: false }] };
    });
  }
  function updateItem(qid, idx, patch) {
    setResponses((r) => {
      const arr = [...(r[qid] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...r, [qid]: arr };
    });
  }
  function removeItem(qid, idx) {
    setResponses((r) => {
      const arr = [...(r[qid] || [])];
      arr.splice(idx, 1);
      return { ...r, [qid]: arr };
    });
  }

  async function saveDraft() {
    try {
      const r = await standupApi.submitList(sessionId, { responses, is_draft: true });
      setSavedAt(r.data.draft_saved_at);
    } catch (_) {}
  }
  async function submitFinal() {
    try {
      await standupApi.submitList(sessionId, { responses, is_draft: false });
      setSubmitted(true);
      toast.success("Submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  }

  // Auto-save draft every 5s when dirty
  const draftDebounce = useRef(null);
  useEffect(() => {
    if (submitted) return;
    if (draftDebounce.current) clearTimeout(draftDebounce.current);
    draftDebounce.current = setTimeout(saveDraft, 5000);
    return () => { if (draftDebounce.current) clearTimeout(draftDebounce.current); };
  }, [responses, submitted]); // eslint-disable-line react-hooks/exhaustive-deps

  const participation = useMemo(() => {
    const ps = progress?.participants || {};
    const total = Object.keys(ps).length || 1;
    const done = Object.values(ps).filter((p) => p.status === "submitted").length;
    return Math.round((done / total) * 100);
  }, [progress]);

  if (authLoading || !user) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFC] py-10 px-6" data-testid="standup-participant">
      {submitted && (
        <div className="max-w-3xl mx-auto rounded-md bg-white shadow border border-slate-200 px-4 py-2 text-sm text-slate-700 flex items-center gap-2 mb-4" data-testid="readonly-banner">
          <Lock size={14} weight="bold" />
          You've submitted your answers and are viewing <span className="text-rose-500">{"{Read-Only}"}</span> mode.
        </div>
      )}
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-[1fr,260px] gap-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-violet-100 grid place-items-center">
              <Rocket size={14} weight="fill" className="text-violet-700" />
            </span>
            <h2 className="text-xl font-bold bg-gradient-to-r from-violet-700 to-sky-500 bg-clip-text text-transparent">
              {meta?.standup_name || "Standup"}
            </h2>
            <span className="ml-2 text-[10px] text-slate-500">Participant Entry · Last updated {savedAt ? new Date(savedAt).toLocaleString() : "—"}</span>
            {!submitted && savedAt && <span className="ml-2 text-[10px] text-emerald-600 font-semibold">Draft saved</span>}
            {submitted && <span className="ml-auto inline-flex items-center rounded bg-amber-100 text-amber-700 text-[10px] font-bold tracking-widest px-1.5 py-0.5">READ ONLY</span>}
          </div>

          <div className="mt-6 space-y-5">
            {questions.length === 0 && <div className="text-sm text-slate-400">Waiting for questions to load…</div>}
            {questions.map((q, i) => {
              const items = responses[q.id] || [];
              return (
                <div key={q.id} className={`rounded-xl border ${submitted ? "border-slate-100 bg-slate-50/50 opacity-70" : "border-slate-200 bg-white"} p-4`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">{q.text}</div>
                    <span className="text-[10px] text-slate-400">{items.length} Added</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {items.map((it, idx) => (
                      <div key={it.id} className="flex items-center gap-2" data-testid={`item-${q.id}-${idx}`}>
                        <input type="checkbox" checked={!!it.done} onChange={(e) => !submitted && updateItem(q.id, idx, { done: e.target.checked })} disabled={submitted} />
                        <Input value={it.text} onChange={(e) => updateItem(q.id, idx, { text: e.target.value })} placeholder="Add a point…" className="h-8 text-sm" disabled={submitted} />
                        <button onClick={() => removeItem(q.id, idx)} className="grid place-items-center h-6 w-6 rounded-full bg-rose-100 text-rose-600 disabled:opacity-30" disabled={submitted}>
                          <XIcon size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                    {!submitted && (
                      <button onClick={() => addItem(q.id)} className="w-full text-left text-xs text-slate-500 border border-dashed border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50" data-testid={`add-item-${q.id}`}>
                        + Add item
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right rail */}
        <aside className="self-start space-y-4">
          <div className="rounded-xl bg-slate-900 text-white p-4">
            <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Meeting Info</div>
            <div className="mt-2 text-xs space-y-1">
              <div className="flex justify-between"><span>Project Name</span><span className="font-semibold">{meta?.standup_name || "—"}</span></div>
              <div className="flex justify-between"><span>Time Remaining</span><span className="font-mono text-rose-400">14:02</span></div>
            </div>
            <div className="mt-3 text-[10px] font-bold tracking-widest text-slate-400 uppercase">Participation <span className="float-right text-white">{participation}%</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-700 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink-400 to-violet-400" style={{ width: `${participation}%` }} />
            </div>
          </div>

          <div className="rounded-xl bg-[linear-gradient(135deg,#7B5CD7,#3A3F8C)] text-white p-4 min-h-[140px] flex flex-col justify-end" data-testid="daily-quote-card">
            <h3 className="text-lg font-bold leading-tight">{quote?.headline || "Focus on the 'Why' today."}</h3>
            <p className="mt-1 text-xs italic opacity-80">"{quote?.quote || "Clarity is the speed of execution."}"</p>
          </div>

          {!submitted ? (
            <Button onClick={submitFinal} className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white text-sm" data-testid="submit-updates-btn">
              Submit Updates →
            </Button>
          ) : (
            <Button onClick={() => setSubmitted(false)} variant="outline" className="w-full h-10 text-sm" data-testid="edit-updates-btn">
              Edit My Updates
            </Button>
          )}
        </aside>
      </div>
    </div>
  );
}

// -------------------- Magic Link Join --------------------

export function StandUpJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null); // "waiting" | "admitted"

  useEffect(() => {
    standupApi.resolveMagic(token).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Invalid link"));
  }, [token]);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  async function join() {
    if (!info) return;
    // We need a session_id — for Phase 3 we just create one if the host is the
    // visitor; otherwise we expect the host to have already created it. The
    // homepage's "Active Sessions" list shows them — fail soft.
    try {
      // Find the latest open session for this standup
      const sessRes = await standupApi.activeSessions();
      const candidates = (sessRes.data.sessions || []).filter((s) => s.standup_id === info.standup_id);
      const target = candidates[0];
      if (!target) {
        toast.error("No active session for this standup right now. Try again when the host opens one.");
        return;
      }
      const r = await standupApi.lobbyRequest(target.session_id, { email, name });
      setStatus(r.data.status);
      if (r.data.status === "admitted") {
        navigate(`/standup/session/${target.session_id}/participant`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    }
  }

  if (err) {
    return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="magic-link-error">{err}</div>;
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFC] px-6" data-testid="standup-join">
      <div className="max-w-sm w-full rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">You've been invited</div>
        <h2 className="mt-2 text-xl font-bold text-slate-900">{info?.name || "Standup"}</h2>
        {info?.description && <p className="mt-1 text-xs text-slate-500">{info.description}</p>}
        {info?.expired && (
          <div className="mt-3 rounded bg-amber-50 text-amber-700 text-xs px-3 py-2">This magic link has expired — ask the host to send a fresh one.</div>
        )}

        <div className="mt-5 space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-9 text-sm" data-testid="join-name" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="h-9 text-sm" data-testid="join-email" />
        </div>

        {status === "waiting" && (
          <div className="mt-4 rounded bg-sky-50 text-sky-700 text-xs px-3 py-2" data-testid="lobby-waiting">
            Waiting for the host to admit you to the room.
          </div>
        )}

        <Button onClick={join} disabled={!email} className="mt-5 w-full h-10 bg-slate-950 hover:bg-slate-800 text-white text-sm" data-testid="join-confirm">
          {info?.auto_join ? "Join the Meeting" : "Request to Join"}
        </Button>
      </div>
    </div>
  );
}

// -------------------- Helpers --------------------

function formatElapsed(iso) {
  if (!iso) return "00:00";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function formatSeconds(s) {
  if (!s) return "00:00m";
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}m`;
}

function hashGradient(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xfff;
  const a = h % 360;
  const b = (a + 60) % 360;
  return `linear-gradient(135deg, hsl(${a}, 70%, 55%), hsl(${b}, 65%, 60%))`;
}
